import { useCallback, useEffect, useState } from 'react';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { isUrlExcluded } from '../utils/urlMatcher';

// Delay for click-based URL check. SPA routers use pushState which doesn't fire popstate,
// so we check after clicks with a delay to let the router complete navigation.
const SPA_NAVIGATION_DETECTION_DELAY_MS = 500;

export interface UrlExclusionResult {
	urlIsExcluded: boolean;
	matchedPattern?: string;
}

export const useUrlExclusion = (): UrlExclusionResult => {
	const [exclusionState, setExclusionState] = useState<UrlExclusionResult>(() => {
		const { excluded, matchedPattern } = isUrlExcluded();
		return { urlIsExcluded: excluded, matchedPattern };
	});

	const checkUrlExclusion = useCallback(() => {
		const { excluded, matchedPattern } = isUrlExcluded();
		setExclusionState((prevState) => {
			if (
				prevState.urlIsExcluded === excluded &&
				prevState.matchedPattern === matchedPattern
			) {
				return prevState;
			}
			return { urlIsExcluded: excluded, matchedPattern };
		});
	}, []);

	useEffect(() => {
		window.addEventListener('popstate', checkUrlExclusion);
		window.addEventListener('hashchange', checkUrlExclusion);

		const handleClick = () => setTimeout(checkUrlExclusion, SPA_NAVIGATION_DETECTION_DELAY_MS);
		window.addEventListener('click', handleClick, { capture: true });

		return () => {
			window.removeEventListener('popstate', checkUrlExclusion);
			window.removeEventListener('hashchange', checkUrlExclusion);
			window.removeEventListener('click', handleClick, { capture: true });
		};
	}, [checkUrlExclusion]);

	return exclusionState;
};
